import { createApp } from 'vue'
import App from './App.vue'
import router from './router';
import {createPinia} from 'pinia';
import {i18n} from "@/composables/i18n";
import { provideApolloClient } from '@vue/apollo-composable'
import { apolloClient } from '@/lib/apollo'

import { IonicVue } from '@ionic/vue';
import {checkThemeState} from "@/actions/getThemesState";

import { useAuthStore } from '@/stores/auth';
import { useAppSettingsStore } from './stores/appSettings';
import { App as CapacitorApp } from '@capacitor/app';
import './pwa/registerServiceWorker';

import {useAppVersion} from "@/composables/useAppVersion";
import { Capacitor } from '@capacitor/core';
import { handleAppResume } from '@/lib/appLifecycle';
import * as Sentry from '@sentry/capacitor';
import * as SentryVue from '@sentry/vue';
import { rewriteFramesIntegration } from '@sentry/core';

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';

/**
 * Ionic Dark Mode
 * -----------------------------------------------------
 * For more info, please see:
 * https://ionicframework.com/docs/theming/dark-mode
 */

/* @import '@ionic/vue/css/palettes/dark.always.css'; */
// import '@ionic/vue/css/palettes/dark.class.css';
// import '@ionic/vue/css/palettes/dark.system.css';

/* Theme variables */
import './theme/variables.css';
import './theme/utilities.css';
import './theme/theme.css';

const pinia = createPinia()

const initApp = async () => {
    const app = createApp(App)
        .use(IonicVue, {
            mode: 'md',
        })
        .use(router)
        .use(pinia)
        .use(i18n);

    provideApolloClient(apolloClient);
    console.log('Created Apollo Client');

    // ✅ Bootstrap order matters: auth -> settings
    const auth = useAuthStore();
    await auth.attemptRestore(); // sets token/locale/version headers if present

    const appSettings = useAppSettingsStore();
    await appSettings.init(); // loads cached + refreshes from API

    // Resume contract: refresh settings + lightweight signed-URL invalidation.
    CapacitorApp.addListener('appStateChange', async (state) => {
        if (state.isActive) {
            await handleAppResume();
        }
    });

    const appVersion = useAppVersion();

    const isNative = Capacitor.isNativePlatform(); // true on iOS/Android, false on PWA/web

    Sentry.init(
        ({
            app,
            dsn: import.meta.env.VITE_SENTRY_DSN,
            release: "kreekvelden-moestuinen-app@" + appVersion.value,
            environment: import.meta.env.VITE_ENV,
            transport: SentryVue.makeBrowserOfflineTransport(SentryVue.makeFetchTransport),
            integrations: [
                ...(isNative ? [rewriteFramesIntegration()] : []),
                SentryVue.httpClientIntegration(),
                SentryVue.browserTracingIntegration({
                    router,
                }),
            ],
            tracesSampleRate: import.meta.env.MODE === 'production' ? 0.2 : 1.0,
            tracePropagationTargets: ['localhost', /^https:\/\/yourapi\.com/],
        } as any),
        SentryVue.init
    );

    Sentry.getGlobalScope().addEventProcessor(function (event) {
        if (!Capacitor.isNativePlatform()) {
            // On PWA, use build-time values — no native info available
            event.release = "kreekvelden-moestuinen-app@" + appVersion.value;
            event.dist = import.meta.env.VITE_DEFAULT_VERSION;
            return Promise.resolve(event);
        }

        return new Promise(function (resolve) {
            CapacitorApp.getInfo().then(info => {
                event.release = `kreekvelden-moestuinen-app@${info.version}+${info.build}`;
                event.dist = info.build;
                return resolve(event);
            }).catch(() => resolve(event));
        });
    });

    await router.isReady();
    app.mount('#app');

    //theme files
    // checkThemeState();
};

initApp();
